import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IconAddressBook,
  IconEdit,
  IconLoader2,
  IconLinkOff,
  IconRefresh,
  IconDeviceFloppy,
  IconSettings,
  IconX,
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import api from '../lib/api';
import { DistributorContactsDrawer } from '../components/DistributorContactsDrawer';
import { UnlinkedContactsDialog } from '../components/UnlinkedContactsDialog';
import PageHeader from '../components/PageHeader';
import { StatCard } from '../components/cards';
import MainCard from '../components/MainCard';

interface Distributor {
  id: number;
  i_customer: number | null;
  group_i_customer: number | null;
  parent_i_customer: number | null;
  parent_id: number | null;
  live: number | null;
  has_referrer: number | null;
  referrer_i_customer: number | null;
  distributor_class: number | null;
  distributor_type: number | null;
  code: string | null;
  company_name: string | null;
  company_registration_number: string | null;
  company_vat_number: string | null;
  id_type: number | null;
  username: string | null;
  contact_name_surname: string | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
  msisdn: string | null;
  whatsapp_sender: string | null;
  email: string | null;
  address_1: string | null;
  address_2: string | null;
  suburb: string | null;
  city: string | null;
  province: number | null;
  zip: string | null;
  sim_activation: string | null;
  product_group_id: number | null;
  commission_ogr: number | null;
  commission_sales: number | null;
  commission_vouchers: number | null;
  commission_voice: number | null;
  commission_sms: number | null;
  commission_referrer: number | null;
  commission_1: string | null;
  commission_2: string | null;
  commission_3: string | null;
  c_commission_1: string | null;
  c_commission_2: string | null;
  c_commission_3: string | null;
  activations: number | null;
  commission_1_mtd: number | null;
  commission_2_mtd: number | null;
  commission_3_mtd: number | null;
  commission_mtd: number | null;
  c_commission_1_mtd: number | null;
  c_commission_2_mtd: number | null;
  c_commission_3_mtd: number | null;
  c_commission_mtd: number | null;
  kustomer_user_id: string | null;
  firebase_push_token: string | null;
  firebase_permission: number | null;
  activation_id: number | null;
  allowed_megsapp_login: number | null;
  allowed_bundle_id_list: string | null;
  clevertap_bundle_id: string | null;
  send_welcome_sms: number | null;
  brand: string | null;
  brand_id: string | null;
  brand_logo: string | null;
  brand_logo_portal: string | null;
  brand_domain: string | null;
  shopify_referral_id: string | null;
  xero_contact_id: string | null;
  monday_id: number | null;
  referrer_code: string | null;
  adbutler_id: string | null;
  adbutler_rewards_ad_id: string | null;
  adbutler_inbox_ad_id: string | null;
  onelink: string | null;
  svix_app: string | null;
  svix_app_id: string | null;
  svix_app_key: string | null;
  encrypt_files: number | null;
  pgp_key: string | null;
  ftp_url: string | null;
  ftp_username: string | null;
  ftp_password: string | null;
  remote_path: string | null;
  wallet_merchant_id: string | null;
  wallet_snsi: string | null;
  wallet_account_number: string | null;
  wallet: number | null;
  require_external_id: number | null;
  report: number | null;
  esim_order_email_template_id: number | null;
  esim_qr_code_email_template_id: number | null;
  promo_data_id: number | null;
  sms_portal_client_id: string | null;
  sms_portal_secret: string | null;
  is_active: number | null;
  tupperware_company_name: string | null;
  rica_limit: number | null;
  created_at: string | null;
  updated_at: string | null;
  [key: string]: any;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface DistributorStats {
  total: number;
  active: number;
  inactive: number;
  filtered: {
    total: number;
    active: number;
    inactive: number;
  };
}

type ActiveFilter = 'all' | 'active' | 'inactive';

const BOOLEAN_FIELDS = new Set([
  'live',
  'has_referrer',
  'firebase_permission',
  'allowed_megsapp_login',
  'send_welcome_sms',
  'encrypt_files',
  'wallet',
  'require_external_id',
  'report',
  'is_active',
]);

const NUMERIC_FIELDS = new Set([
  'i_customer',
  'group_i_customer',
  'parent_i_customer',
  'parent_id',
  'referrer_i_customer',
  'distributor_class',
  'distributor_type',
  'id_type',
  'province',
  'product_group_id',
  'commission_ogr',
  'commission_sales',
  'commission_vouchers',
  'commission_voice',
  'commission_sms',
  'commission_referrer',
  'activations',
  'commission_1_mtd',
  'commission_2_mtd',
  'commission_3_mtd',
  'commission_mtd',
  'c_commission_1_mtd',
  'c_commission_2_mtd',
  'c_commission_3_mtd',
  'c_commission_mtd',
  'activation_id',
  'monday_id',
  'esim_order_email_template_id',
  'esim_qr_code_email_template_id',
  'promo_data_id',
  'rica_limit',
]);

const READ_ONLY_FIELDS = new Set(['id', 'created_at', 'updated_at']);

const formatDateTime = (value: string | null) => {
  if (!value) return '-';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString();
  } catch {
    return value;
  }
};

const distributorDetailGroups: Record<
  string,
  { key: keyof Distributor; label: string; multiline?: boolean }[]
> = {
  General: [
    { key: 'company_name', label: 'Company Name' },
    { key: 'code', label: 'Code' },
    { key: 'company_registration_number', label: 'Company Registration #' },
    { key: 'company_vat_number', label: 'VAT Number' },
    { key: 'brand', label: 'Brand' },
    { key: 'brand_id', label: 'Brand ID' },
    { key: 'brand_domain', label: 'Brand Domain' },
    { key: 'brand_logo', label: 'Brand Logo URL' },
    { key: 'brand_logo_portal', label: 'Brand Logo (Portal) URL' },
  ],
  Identifiers: [
    { key: 'i_customer', label: 'i_customer' },
    { key: 'group_i_customer', label: 'group_i_customer' },
    { key: 'parent_i_customer', label: 'parent_i_customer' },
    { key: 'parent_id', label: 'parent_id' },
    { key: 'username', label: 'Username' },
    { key: 'id_type', label: 'ID Type' },
    { key: 'distributor_class', label: 'Distributor Class' },
    { key: 'distributor_type', label: 'Distributor Type' },
    { key: 'activation_id', label: 'Activation ID' },
    { key: 'product_group_id', label: 'Product Group ID' },
    { key: 'promo_data_id', label: 'Promo Data ID' },
  ],
  Contact: [
    { key: 'contact_first_name', label: 'Contact First Name' },
    { key: 'contact_last_name', label: 'Contact Last Name' },
    { key: 'contact_name_surname', label: 'Contact Full Name' },
    { key: 'email', label: 'Email' },
    { key: 'msisdn', label: 'MSISDN' },
    { key: 'whatsapp_sender', label: 'WhatsApp Sender' },
    { key: 'tupperware_company_name', label: 'Tupperware Company Name' },
  ],
  Address: [
    { key: 'address_1', label: 'Address Line 1', multiline: true },
    { key: 'address_2', label: 'Address Line 2', multiline: true },
    { key: 'suburb', label: 'Suburb' },
    { key: 'city', label: 'City' },
    { key: 'province', label: 'Province' },
    { key: 'zip', label: 'ZIP / Postal Code' },
  ],
  Credentials: [
    { key: 'ftp_url', label: 'FTP URL' },
    { key: 'ftp_username', label: 'FTP Username' },
    { key: 'ftp_password', label: 'FTP Password' },
    { key: 'remote_path', label: 'Remote Path' },
    { key: 'wallet_merchant_id', label: 'Wallet Merchant ID' },
    { key: 'wallet_snsi', label: 'Wallet SNSI' },
    { key: 'wallet_account_number', label: 'Wallet Account Number' },
    { key: 'sms_portal_client_id', label: 'SMS Portal Client ID' },
    { key: 'sms_portal_secret', label: 'SMS Portal Secret' },
    { key: 'shopify_referral_id', label: 'Shopify Referral ID' },
    { key: 'xero_contact_id', label: 'Xero Contact ID' },
    { key: 'monday_id', label: 'Monday.com Board ID' },
    { key: 'svix_app', label: 'Svix App' },
    { key: 'svix_app_id', label: 'Svix App ID' },
    { key: 'svix_app_key', label: 'Svix App Key' },
  ],
  Communications: [
    { key: 'allowed_bundle_id_list', label: 'Allowed Bundle IDs', multiline: true },
    { key: 'clevertap_bundle_id', label: 'Clevertap Bundle ID' },
    { key: 'onelink', label: 'Onelink URL' },
    { key: 'kustomer_user_id', label: 'Kustomer User ID' },
    { key: 'firebase_push_token', label: 'Firebase Push Token', multiline: true },
  ],
  Commission: [
    { key: 'commission_ogr', label: 'Commission OGR' },
    { key: 'commission_sales', label: 'Commission Sales' },
    { key: 'commission_vouchers', label: 'Commission Vouchers' },
    { key: 'commission_voice', label: 'Commission Voice' },
    { key: 'commission_sms', label: 'Commission SMS' },
    { key: 'commission_referrer', label: 'Commission Referrer' },
    { key: 'commission_1', label: 'Commission Tier 1' },
    { key: 'commission_2', label: 'Commission Tier 2' },
    { key: 'commission_3', label: 'Commission Tier 3' },
    { key: 'c_commission_1', label: 'Child Commission Tier 1' },
    { key: 'c_commission_2', label: 'Child Commission Tier 2' },
    { key: 'c_commission_3', label: 'Child Commission Tier 3' },
  ],
  Performance: [
    { key: 'activations', label: 'Activations' },
    { key: 'commission_1_mtd', label: 'Commission 1 MTD' },
    { key: 'commission_2_mtd', label: 'Commission 2 MTD' },
    { key: 'commission_3_mtd', label: 'Commission 3 MTD' },
    { key: 'commission_mtd', label: 'Commission Total MTD' },
    { key: 'c_commission_1_mtd', label: 'Child Commission 1 MTD' },
    { key: 'c_commission_2_mtd', label: 'Child Commission 2 MTD' },
    { key: 'c_commission_3_mtd', label: 'Child Commission 3 MTD' },
    { key: 'c_commission_mtd', label: 'Child Commission Total MTD' },
  ],
  Flags: [
    { key: 'live', label: 'Live' },
    { key: 'has_referrer', label: 'Has Referrer' },
    { key: 'firebase_permission', label: 'Firebase Permission' },
    { key: 'allowed_megsapp_login', label: 'Allowed megsApp Login' },
    { key: 'send_welcome_sms', label: 'Send Welcome SMS' },
    { key: 'encrypt_files', label: 'Encrypt Files' },
    { key: 'wallet', label: 'Wallet Enabled' },
    { key: 'require_external_id', label: 'Require External ID' },
    { key: 'report', label: 'Report Enabled' },
    { key: 'sim_activation', label: 'SIM Activation' },
    { key: 'esim_order_email_template_id', label: 'eSIM Order Template ID' },
    { key: 'esim_qr_code_email_template_id', label: 'eSIM QR Code Template ID' },
    { key: 'rica_limit', label: 'RICA Limit' },
    { key: 'is_active', label: 'Active' },
  ],
  Security: [
    { key: 'pgp_key', label: 'PGP Key', multiline: true },
    { key: 'referrer_code', label: 'Referrer Code' },
    { key: 'adbutler_id', label: 'AdButler ID' },
    { key: 'adbutler_rewards_ad_id', label: 'AdButler Rewards Ad ID' },
    { key: 'adbutler_inbox_ad_id', label: 'AdButler Inbox Ad ID' },
  ],
};

export function DistributorsPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('active');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState<Partial<Distributor>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [searchOptions, setSearchOptions] = useState<Distributor[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [stats, setStats] = useState<DistributorStats | null>(null);
  const [contactsDrawerOpen, setContactsDrawerOpen] = useState(false);
  const [contactsDistributorId, setContactsDistributorId] = useState<number | null>(null);
  const [contactsDistributorICustomer, setContactsDistributorICustomer] = useState<number | null>(null);
  const [contactsDistributorName, setContactsDistributorName] = useState<string | null>(null);
  const [unlinkedContactsOpen, setUnlinkedContactsOpen] = useState(false);

  const searchDebounceRef = useRef<number | null>(null);
  const searchRequestIdRef = useRef(0);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDistributors = useCallback(
    async (override?: { search?: string; page?: number; limit?: number; activeFilter?: ActiveFilter }) => {
      const searchTerm = override?.search ?? search;
      const currentPage = override?.page ?? page;
      const limit = override?.limit ?? rowsPerPage;
      const filter = override?.activeFilter ?? activeFilter;

      setLoading(true);
      setError(null);

      try {
        const params: Record<string, any> = {
          page: currentPage + 1,
          limit,
        };

        if (searchTerm.trim()) {
          params.search = searchTerm.trim();
        }

        if (filter === 'active') {
          params.isActive = true;
        } else if (filter === 'inactive') {
          params.isActive = false;
        }

        const response = await api.get('/distributors', { params });
        const { data, pagination: pager, stats: serverStats } = response.data;

        setDistributors(data);
        setPagination(pager);
        setPage(Math.max(0, (pager?.page ?? 1) - 1));
        setRowsPerPage(pager?.limit ?? limit);
        if (serverStats) {
          setStats({
            total: Number(serverStats.total ?? 0),
            active: Number(serverStats.active ?? 0),
            inactive: Number(serverStats.inactive ?? 0),
            filtered: {
              total: Number(serverStats.filtered?.total ?? pager?.total ?? 0),
              active: Number(serverStats.filtered?.active ?? 0),
              inactive: Number(serverStats.filtered?.inactive ?? 0),
            },
          });
        } else {
          setStats(null);
        }
      } catch (err: any) {
        console.error('Failed to load distributors', err);
        setError(err.response?.data?.message || 'Failed to load distributors');
      } finally {
        setLoading(false);
      }
    },
    [activeFilter, page, rowsPerPage, search],
  );

  useEffect(() => {
    fetchDistributors();
  }, [fetchDistributors]);

  const fetchSearchOptions = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (trimmed.length < 2) {
        searchRequestIdRef.current += 1;
        setSearchOptions([]);
        setSearchLoading(false);
        return;
      }

      const requestId = Date.now();
      searchRequestIdRef.current = requestId;
      setSearchLoading(true);

      try {
        const response = await api.get('/distributors', {
          params: {
            search: trimmed,
            limit: 10,
            page: 1,
          },
        });

        if (searchRequestIdRef.current === requestId) {
          setSearchOptions(response.data?.data ?? []);
          setShowSuggestions(true);
        }
      } catch (err) {
        console.error('Failed to load distributor suggestions', err);
        if (searchRequestIdRef.current === requestId) {
          setSearchOptions([]);
        }
      } finally {
        if (searchRequestIdRef.current === requestId) {
          setSearchLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleSearch = () => {
    const nextSearch = searchInput.trim();
    setPage(0);
    setSearch(nextSearch);
    setShowSuggestions(false);
    fetchDistributors({ search: nextSearch, page: 0 });
  };

  const handleRefresh = () => {
    setSearchInput(search);
    fetchDistributors();
    setSearchOptions([]);
    setShowSuggestions(false);
  };

  const handleSelectSuggestion = (option: Distributor) => {
    const label = option.company_name ?? option.code ?? option.username ?? '';
    setSearchInput(label);
    setSearch(label);
    setPage(0);
    setShowSuggestions(false);
    fetchDistributors({ search: label, page: 0 });
  };

  const totalPages = pagination ? pagination.pages : 0;

  const loadDistributorDetails = useCallback(
    async (id: number) => {
      setDetailLoading(true);
      setDetailError(null);
      setSelectedDistributor(null);
      setEditValues({});
      setSaveSuccess(null);
      setSaveError(null);

      try {
        const response = await api.get(`/distributors/${id}`);
        const distributor = response.data?.data ?? response.data;
        setSelectedDistributor(distributor);
        setEditValues(distributor);
      } catch (err: any) {
        console.error('Failed to load distributor details', err);
        setDetailError(err.response?.data?.message || 'Failed to load distributor details');
      } finally {
        setDetailLoading(false);
      }
    },
    [],
  );

  const handleSelectDistributor = (id: number) => {
    setSelectedId(id);
    setEditMode(false);
    loadDistributorDetails(id);
  };

  const handleManageContacts = (dist: Distributor, event: React.MouseEvent) => {
    event.stopPropagation();
    setContactsDistributorId(dist.id);
    setContactsDistributorICustomer(dist.i_customer);
    setContactsDistributorName(dist.company_name);
    setContactsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setSelectedId(null);
    setSelectedDistributor(null);
    setEditMode(false);
    setEditValues({});
    setDetailError(null);
    setSaveError(null);
    setSaveSuccess(null);
  };

  const handleFieldChange = (key: keyof Distributor, value: any) => {
    setEditValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const normalizedValue = useCallback((value: any) => {
    if (value === undefined) return null;
    if (value === '') return '';
    return value;
  }, []);

  const computeChanges = useCallback(() => {
    if (!selectedDistributor) {
      return {};
    }

    const changes: Record<string, any> = {};

    for (const [key, value] of Object.entries(editValues)) {
      if (READ_ONLY_FIELDS.has(key)) {
        continue;
      }

      const originalValue = normalizedValue(selectedDistributor[key as keyof Distributor]);
      let nextValue: any = normalizedValue(value);

      if (BOOLEAN_FIELDS.has(key)) {
        nextValue = nextValue ? 1 : 0;
      } else if (NUMERIC_FIELDS.has(key)) {
        if (nextValue === null || nextValue === '') {
          nextValue = null;
        } else {
          const parsed = Number(nextValue);
          nextValue = Number.isNaN(parsed) ? nextValue : parsed;
        }
      } else if (nextValue === '') {
        nextValue = null;
      }

      const originalComparable =
        originalValue === null || originalValue === undefined ? null : originalValue;
      const nextComparable = nextValue === undefined ? null : nextValue;

      if (originalComparable instanceof Date) {
        continue;
      }

      if (originalComparable !== nextComparable) {
        changes[key] = nextValue;
      }
    }

    return changes;
  }, [editValues, normalizedValue, selectedDistributor]);

  const changes = useMemo(() => computeChanges(), [computeChanges]);
  const hasChanges = Object.keys(changes).length > 0;

  const handleSave = async () => {
    if (!selectedId) {
      return;
    }

    const payload = computeChanges();

    if (Object.keys(payload).length === 0) {
      setSaveError('No changes to save');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const response = await api.patch(`/distributors/${selectedId}`, {
        payload,
      });

      const updatedDistributor = response.data?.data ?? response.data;
      setSelectedDistributor(updatedDistributor);
      setEditValues(updatedDistributor);
      setSaveSuccess('Distributor updated successfully');
      setEditMode(false);

      setDistributors((prev) =>
        prev.map((dist) => (dist.id === selectedId ? { ...dist, ...updatedDistributor } : dist)),
      );
    } catch (err: any) {
      console.error('Failed to update distributor', err);
      setSaveError(err.response?.data?.message || 'Failed to update distributor');
    } finally {
      setSaving(false);
      fetchDistributors();
    }
  };

  const renderFieldInput = (fieldKey: keyof Distributor, label: string, multiline?: boolean) => {
    const value = editValues[fieldKey];
    const isBoolean = BOOLEAN_FIELDS.has(fieldKey as string);
    const isNumeric = NUMERIC_FIELDS.has(fieldKey as string);
    const readOnly = READ_ONLY_FIELDS.has(fieldKey as string);

    if (!editMode || readOnly) {
      if (fieldKey === 'created_at' || fieldKey === 'updated_at') {
        return (
          <div className="space-y-1.5">
            <Label>{label}</Label>
            <Input
              value={formatDateTime(selectedDistributor?.[fieldKey] ?? null)}
              readOnly
            />
          </div>
        );
      }

      if (isBoolean) {
        const boolValue = Boolean(Number(selectedDistributor?.[fieldKey] ?? 0));
        return (
          <div className="flex items-center gap-2 py-2">
            <Switch checked={boolValue} disabled />
            <Label className="opacity-60">{label}</Label>
          </div>
        );
      }

      if (multiline) {
        return (
          <div className="space-y-1.5">
            <Label>{label}</Label>
            <Textarea
              value={String(selectedDistributor?.[fieldKey] ?? '')}
              readOnly
              rows={3}
            />
          </div>
        );
      }

      return (
        <div className="space-y-1.5">
          <Label>{label}</Label>
          <Input
            value={String(selectedDistributor?.[fieldKey] ?? '')}
            readOnly
          />
        </div>
      );
    }

    if (isBoolean) {
      const boolValue = Boolean(Number(value ?? selectedDistributor?.[fieldKey] ?? 0));
      return (
        <div className="flex items-center gap-2 py-2">
          <Switch
            checked={boolValue}
            onCheckedChange={(checked) => handleFieldChange(fieldKey, checked ? 1 : 0)}
          />
          <Label>{label}</Label>
        </div>
      );
    }

    if (multiline) {
      return (
        <div className="space-y-1.5">
          <Label>{label}</Label>
          <Textarea
            value={String(
              value ??
              (selectedDistributor?.[fieldKey] !== null && selectedDistributor?.[fieldKey] !== undefined
                ? String(selectedDistributor?.[fieldKey])
                : '')
            )}
            rows={4}
            onChange={(event) => handleFieldChange(fieldKey, event.target.value)}
          />
        </div>
      );
    }

    return (
      <div className="space-y-1.5">
        <Label>{label}</Label>
        <Input
          value={String(
            value ??
            (selectedDistributor?.[fieldKey] !== null && selectedDistributor?.[fieldKey] !== undefined
              ? String(selectedDistributor?.[fieldKey])
              : '')
          )}
          type={isNumeric ? 'number' : 'text'}
          onChange={(event) => handleFieldChange(fieldKey, event.target.value)}
        />
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        title="Distributor Management"
        subtitle="Search, review, and update distributor records from the distributors table."
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setUnlinkedContactsOpen(true)}
            >
              <IconLinkOff size={16} />
              Unlinked Contacts
            </Button>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={loading}
            >
              <IconRefresh size={16} />
              Refresh
            </Button>
          </div>
        }
      />

      {stats && (
        <div className="flex gap-6 mb-6 w-full">
          <div className="flex-1 min-w-0">
            <StatCard
              title="Total Distributors"
              value={stats.total.toLocaleString()}
            />
          </div>
          <div className="flex-1 min-w-0">
            <StatCard
              title="Active Distributors"
              value={stats.active.toLocaleString()}
            />
          </div>
          <div className="flex-1 min-w-0">
            <StatCard
              title="Inactive / Pending"
              value={stats.inactive.toLocaleString()}
            />
          </div>
        </div>
      )}

      <MainCard className="mb-6">
        <div className="flex flex-col md:flex-row gap-4 flex-wrap items-center">
          <div className="relative flex-1 min-w-0 md:min-w-[380px] w-full" ref={suggestionsRef}>
            <div className="relative">
              <Input
                placeholder="Start typing a company name, code, or username"
                value={searchInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchInput(val);
                  if (searchDebounceRef.current) {
                    window.clearTimeout(searchDebounceRef.current);
                  }
                  if (!val || val.trim().length < 2) {
                    setSearchOptions([]);
                    setSearchLoading(false);
                    setShowSuggestions(false);
                    return;
                  }
                  searchDebounceRef.current = window.setTimeout(() => {
                    fetchSearchOptions(val);
                  }, 300);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleSearch();
                  }
                }}
                onFocus={() => {
                  if (searchOptions.length > 0) setShowSuggestions(true);
                }}
              />
              {searchLoading && (
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  <IconLoader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
            {showSuggestions && searchOptions.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-64 overflow-auto rounded-lg border bg-popover shadow-md">
                {searchOptions.map((option) => (
                  <div
                    key={option.id}
                    className="cursor-pointer px-3 py-2 hover:bg-muted"
                    onClick={() => handleSelectSuggestion(option)}
                  >
                    <p className="text-sm font-semibold">
                      {option.company_name || 'Unnamed Distributor'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {[
                        option.code ? `Code: ${option.code}` : null,
                        option.i_customer ? `i_customer: ${option.i_customer}` : null,
                        option.username ? `Username: ${option.username}` : null,
                      ]
                        .filter(Boolean)
                        .join(' \u2022 ')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Select
            value={activeFilter}
            onValueChange={(value) => {
              const val = value as ActiveFilter;
              setActiveFilter(val);
              setPage(0);
              fetchDistributors({ activeFilter: val, page: 0 });
            }}
          >
            <SelectTrigger className="min-w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Inactive only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </MainCard>

      <MainCard>
        {loading && (
          <div className="flex items-center justify-center py-16">
            <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {!loading && error && (
          <div className="p-4">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        {!loading && !error && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Customer ID</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Comm OGR</TableHead>
                  <TableHead>Comm Sales</TableHead>
                  <TableHead>Comm Vouchers</TableHead>
                  <TableHead>Comm Referrer</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {distributors.map((dist) => (
                  <TableRow
                    key={dist.id}
                    className={cn(
                      'cursor-pointer',
                      dist.id === selectedId && 'bg-muted',
                    )}
                    onClick={() => handleSelectDistributor(dist.id)}
                  >
                    <TableCell>{dist.company_name ?? '-'}</TableCell>
                    <TableCell>{dist.i_customer ?? '-'}</TableCell>
                    <TableCell>{dist.brand ?? '-'}</TableCell>
                    <TableCell>{dist.commission_ogr ?? '-'}</TableCell>
                    <TableCell>{dist.commission_sales ?? '-'}</TableCell>
                    <TableCell>{dist.commission_vouchers ?? '-'}</TableCell>
                    <TableCell>{dist.commission_referrer ?? '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={(e) => handleManageContacts(dist, e)}
                          title="Manage Contacts"
                        >
                          <IconAddressBook size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          disabled
                          title="Manage All Fields (Coming Soon)"
                        >
                          <IconSettings size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {distributors.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <div className="py-12 text-center">
                        <p className="text-muted-foreground">
                          No distributors found for the current filters.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {pagination && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Rows per page:</span>
                  <Select
                    value={String(rowsPerPage)}
                    onValueChange={(val) => {
                      if (val) {
                        const newLimit = parseInt(val, 10);
                        setRowsPerPage(newLimit);
                        setPage(0);
                        fetchDistributors({ limit: newLimit, page: 0 });
                      }
                    }}
                  >
                    <SelectTrigger className="w-[70px]" size="sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 25, 50, 100].map((opt) => (
                        <SelectItem key={opt} value={String(opt)}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>
                    {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, pagination.total)} of {pagination.total}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => setPage(0)} disabled={page === 0}>
                      First
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                      Prev
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                      Next
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>
                      Last
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </MainCard>

      <Sheet open={Boolean(selectedId)} onOpenChange={(open) => { if (!open) handleCloseDrawer(); }}>
        <SheetContent side="right" className="w-full sm:max-w-none sm:w-[85vw] md:w-[75vw] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {selectedDistributor?.company_name ?? 'Distributor Details'}
            </SheetTitle>
            {selectedDistributor?.code && (
              <SheetDescription>
                Code: {selectedDistributor.code}
              </SheetDescription>
            )}
          </SheetHeader>
          <Separator />

          <div className="p-4">
            {detailLoading && (
              <div className="py-12 flex justify-center">
                <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}

            {!detailLoading && detailError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{detailError}</AlertDescription>
              </Alert>
            )}

            {!detailLoading && selectedDistributor && (
              <>
                <div className="flex flex-col sm:flex-row justify-between gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editMode}
                      onCheckedChange={(checked) => {
                        setEditMode(checked);
                        setSaveError(null);
                        setSaveSuccess(null);
                        setEditValues(selectedDistributor);
                      }}
                    />
                    <Label>Enable editing</Label>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSave}
                      disabled={!editMode || !hasChanges || saving}
                    >
                      {saving ? <IconLoader2 size={16} className="animate-spin" /> : <IconDeviceFloppy size={16} />}
                      Save changes
                    </Button>
                    <Button
                      variant="outline"
                      disabled={saving}
                      onClick={() => {
                        setEditValues(selectedDistributor);
                        setSaveError(null);
                        setSaveSuccess(null);
                      }}
                    >
                      <IconEdit size={16} />
                      Reset
                    </Button>
                  </div>
                </div>

                {saveError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{saveError}</AlertDescription>
                  </Alert>
                )}
                {saveSuccess && (
                  <Alert variant="success" className="mb-4">
                    <AlertDescription>{saveSuccess}</AlertDescription>
                  </Alert>
                )}

                <Alert variant="info" className="mb-4">
                  <AlertDescription>
                    ID: <strong>{selectedDistributor.id}</strong> &bull; Created:{' '}
                    {formatDateTime(selectedDistributor.created_at)} &bull; Updated:{' '}
                    {formatDateTime(selectedDistributor.updated_at)}
                  </AlertDescription>
                </Alert>

                <Accordion defaultValue={Object.keys(distributorDetailGroups)}>
                  {Object.entries(distributorDetailGroups).map(([groupName, fields]) => (
                    <AccordionItem key={groupName} value={groupName}>
                      <AccordionTrigger>
                        <span className="text-sm font-semibold">{groupName}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {fields.map(({ key, label, multiline }) => (
                            <div key={key as string} className={cn(multiline && 'sm:col-span-2')}>
                              {renderFieldInput(key, label, multiline)}
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <DistributorContactsDrawer
        open={contactsDrawerOpen}
        onClose={() => setContactsDrawerOpen(false)}
        distributorId={contactsDistributorId}
        distributorICustomer={contactsDistributorICustomer}
        distributorName={contactsDistributorName}
      />

      <UnlinkedContactsDialog
        open={unlinkedContactsOpen}
        onClose={() => setUnlinkedContactsOpen(false)}
      />
    </div>
  );
}
